import { useState, useMemo, useEffect, Fragment } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { computeAssembly } from "@/utils/assemblyCalc";
import { computeNpd } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";
import RoundCompareView from "@/components/RoundCompareView";
import api from "@/lib/api";

export default function AssemblyCompareView({ round, currentAsm, currentComponents, rightLabel = "Current", assemblyId }) {
  const [expanded, setExpanded] = useState({});
  const [drillNpd, setDrillNpd] = useState(null);
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const savedAsm = round?.snapshot?.assembly || {};
  const savedComponents = round?.snapshot?.components || [];

  const thenCalc = useMemo(() => computeAssembly(savedAsm, savedComponents), [savedAsm, savedComponents]);
  const nowCalc = useMemo(() => computeAssembly(currentAsm || {}, currentComponents || []), [currentAsm, currentComponents]);

  const computeDelta = (a, b) => {
    const d = (Number(b) || 0) - (Number(a) || 0);
    const pct = Math.abs(a) > 0.0001 ? (d / a) * 100 : 0;
    return { abs: d, pct };
  };

  const DeltaAbs = ({ d }) => {
    if (Math.abs(d) < 0.005) return <span className="text-slate-400">—</span>;
    const sign = d > 0 ? "+" : "-";
    const color = d < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{formatINR(Math.abs(d))}</span>;
  };
  const DeltaPct = ({ pct }) => {
    if (Math.abs(pct) < 0.05) return <span className="text-slate-400">—</span>;
    const sign = pct > 0 ? "+" : "";
    const color = pct < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{pct.toFixed(1)}%</span>;
  };

  // Build per-component row data. Match by npd_id; mark added/removed.
  const componentRows = useMemo(() => {
    const savedByNpd = new Map(savedComponents.map((c) => [c.npd_id, c]));
    const currentByNpd = new Map((currentComponents || []).map((c) => [c.npd_id, c]));
    const allNpdIds = Array.from(new Set([
      ...(savedAsm.components || []).map((c) => c.npd_id),
      ...((currentAsm && currentAsm.components) || []).map((c) => c.npd_id),
    ]));
    return allNpdIds.filter(Boolean).map((npd_id) => {
      const savedRef = (savedAsm.components || []).find((c) => c.npd_id === npd_id);
      const curRef   = ((currentAsm && currentAsm.components) || []).find((c) => c.npd_id === npd_id);
      const savedNpd = savedByNpd.get(npd_id);
      const curNpd   = currentByNpd.get(npd_id);
      const thenLine = savedRef && savedNpd ? (Number(savedRef.qty_per_assembly || 0) * Number(computeNpd(savedNpd).grand_total || 0)) : 0;
      const nowLine  = curRef && curNpd ? (Number(curRef.qty_per_assembly || 0) * Number(computeNpd(curNpd).grand_total || 0)) : 0;
      const delta = computeDelta(thenLine, nowLine);
      return {
        npd_id,
        npd_number: (savedNpd || curNpd || {}).npd_number || "(unknown)",
        part_name:  (savedNpd || curNpd || {}).part_name || "",
        thenQty:  savedRef ? Number(savedRef.qty_per_assembly || 0) : null,
        nowQty:   curRef ? Number(curRef.qty_per_assembly || 0) : null,
        thenLine,
        nowLine,
        delta,
        added:   !savedRef && !!curRef,
        removed: !!savedRef && !curRef,
        savedNpd,
        curNpd,
      };
    });
  }, [savedAsm, currentAsm, savedComponents, currentComponents]);

  // Section breakdown for assembly's OWN costs (B' + C' + D' + E').
  const sections = [
    { key: "components", label: "Components Total", thenVal: thenCalc.components_total, nowVal: nowCalc.components_total, drillable: true },
    { key: "assembly_process_cost", label: "B′. Assembly Process Cost", thenVal: thenCalc.assembly_process_cost, nowVal: nowCalc.assembly_process_cost },
    { key: "assembly_bop_cost", label: "C′. Assembly Bought Out Parts", thenVal: thenCalc.assembly_bop_cost, nowVal: nowCalc.assembly_bop_cost },
    { key: "assembly_treatment_total", label: "D′. Assembly Treatment + Tool Maint", thenVal: (thenCalc.assembly_treatment_total || 0) + (thenCalc.assembly_tool_maintenance || 0), nowVal: (nowCalc.assembly_treatment_total || 0) + (nowCalc.assembly_tool_maintenance || 0) },
    { key: "assembly_overheads_total", label: "E′. Assembly Overheads & Margin", thenVal: thenCalc.overheads_and_margin, nowVal: nowCalc.overheads_and_margin },
  ];

  const totalDelta = computeDelta(thenCalc.grand_total, nowCalc.grand_total);

  return (
    <div className="mt-3 bg-white border border-sky-300 rounded-md overflow-hidden" data-testid="assembly-compare-view">
      <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 text-[11px] text-slate-600 flex items-center gap-3 flex-wrap">
        <span><strong className="text-sky-700">{round.round_label}</strong> vs {rightLabel}</span>
        <span className="text-slate-500">·</span>
        <span>Saved {new Date(round.round_date).toLocaleString("en-IN")}</span>
        {round.created_by_name && (<><span className="text-slate-500">·</span><span>by {round.created_by_name}</span></>)}
        {round.notes && (<><span className="text-slate-500">·</span><span className="italic truncate">"{round.notes}"</span></>)}
      </div>

      {/* R49 — Per-round assembly drawing screenshot */}
      {assemblyId && savedAsm?.assembly_drawing_screenshot_path && (
        <AssemblyRoundDrawingStrip assemblyId={assemblyId} round={round} />
      )}

      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left p-2">Section</th>
            <th className="text-right p-2 w-28">Then ₹</th>
            <th className="text-right p-2 w-28">Now ₹</th>
            <th className="text-right p-2 w-28">Δ ₹</th>
            <th className="text-right p-2 w-20">Δ %</th>
            <th className="w-10 p-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr className="bg-slate-100 border-b-2 border-slate-300">
            <td className="p-2 font-bold text-slate-900">ASSEMBLY GRAND TOTAL</td>
            <td className="p-2 text-right font-mono font-bold text-slate-700">{formatINR(thenCalc.grand_total)}</td>
            <td className="p-2 text-right font-mono font-bold text-slate-900">{formatINR(nowCalc.grand_total)}</td>
            <td className="p-2 text-right font-mono font-bold"><DeltaAbs d={totalDelta.abs} /></td>
            <td className="p-2 text-right font-mono font-bold"><DeltaPct pct={totalDelta.pct} /></td>
            <td></td>
          </tr>

          {sections.map((s) => {
            const d = computeDelta(s.thenVal, s.nowVal);
            const isLarge = Math.abs(d.pct) >= 10;
            const isExpanded = expanded[s.key];
            return (
              <Fragment key={s.key}>
                <tr className={isLarge ? "bg-amber-50/60" : "hover:bg-slate-50"}>
                  <td className="p-2">
                    {s.drillable ? (
                      <button type="button" onClick={() => toggle(s.key)} className="flex items-center gap-1 text-left" data-testid={`asm-compare-section-${s.key}`}>
                        {isExpanded ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
                        <span className="font-medium text-slate-700">{s.label}</span>
                      </button>
                    ) : (
                      <span className="font-medium text-slate-700 pl-4">{s.label}</span>
                    )}
                  </td>
                  <td className="p-2 text-right font-mono text-slate-600">{formatINR(s.thenVal)}</td>
                  <td className="p-2 text-right font-mono text-slate-900">{formatINR(s.nowVal)}</td>
                  <td className="p-2 text-right font-mono"><DeltaAbs d={d.abs} /></td>
                  <td className="p-2 text-right font-mono"><DeltaPct pct={d.pct} /></td>
                  <td className="p-2 text-center">
                    {isLarge && (
                      <div className="relative group inline-block">
                        <AlertTriangle size={14} className="text-amber-600 cursor-help" data-testid={`asm-warn-${s.key}`} />
                        <div className="hidden group-hover:block absolute z-20 right-0 top-5 w-64 p-2 bg-slate-800 text-white text-[11px] rounded shadow-lg leading-snug">
                          {s.label} moved {d.pct.toFixed(1)}%. {s.drillable ? "Expand to see which components drove it." : "Check the assembly's own cost inputs."}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                {s.drillable && isExpanded && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50/60 p-2 border-t border-slate-200">
                      {componentRows.length === 0 ? (
                        <div className="text-xs text-slate-400 italic py-2">No components in either snapshot.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="text-left py-1">Component</th>
                              <th className="text-right py-1 w-20">Qty Then</th>
                              <th className="text-right py-1 w-20">Qty Now</th>
                              <th className="text-right py-1 w-24">Then ₹</th>
                              <th className="text-right py-1 w-24">Now ₹</th>
                              <th className="text-right py-1 w-24">Δ ₹</th>
                              <th className="text-right py-1 w-20">Δ %</th>
                              <th className="w-8"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {componentRows.map((row) => {
                              const isCompLarge = Math.abs(row.delta.pct) >= 10;
                              const canDrill = row.savedNpd && row.curNpd;
                              return (
                                <Fragment key={row.npd_id}>
                                  <tr className={isCompLarge ? "bg-amber-50/60" : ""}>
                                    <td className="py-1 px-1">
                                      <button type="button" disabled={!canDrill} onClick={() => setDrillNpd(drillNpd === row.npd_id ? null : row.npd_id)} className="flex items-center gap-1 text-left disabled:opacity-50" data-testid={`asm-compare-comp-${row.npd_id}`}>
                                        {canDrill && (drillNpd === row.npd_id ? <ChevronDown size={11} className="text-slate-400" /> : <ChevronRight size={11} className="text-slate-400" />)}
                                        <span>
                                          <span className="font-mono text-[10px] text-slate-500 mr-1.5">{row.npd_number}</span>
                                          <span className="text-slate-700">{row.part_name}</span>
                                          {row.added && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-700 px-1 rounded">added</span>}
                                          {row.removed && <span className="ml-1 text-[10px] bg-red-100 text-red-700 px-1 rounded">removed</span>}
                                        </span>
                                      </button>
                                    </td>
                                    <td className="py-1 px-1 text-right font-mono text-slate-500">{row.thenQty == null ? "—" : row.thenQty}</td>
                                    <td className="py-1 px-1 text-right font-mono text-slate-700">{row.nowQty == null ? "—" : row.nowQty}</td>
                                    <td className="py-1 px-1 text-right font-mono text-slate-500">{formatINR(row.thenLine)}</td>
                                    <td className="py-1 px-1 text-right font-mono text-slate-900">{formatINR(row.nowLine)}</td>
                                    <td className="py-1 px-1 text-right font-mono"><DeltaAbs d={row.delta.abs} /></td>
                                    <td className="py-1 px-1 text-right font-mono"><DeltaPct pct={row.delta.pct} /></td>
                                    <td className="py-1 px-1 text-center">
                                      {isCompLarge && <AlertTriangle size={12} className="text-amber-600 inline" />}
                                    </td>
                                  </tr>
                                  {drillNpd === row.npd_id && canDrill && (
                                    <tr>
                                      <td colSpan={8} className="bg-white p-2 border-t border-slate-200">
                                        <RoundCompareView
                                          round={{ round_id: `asm-${round.round_id}-${row.npd_id}`, round_label: round.round_label, round_date: round.round_date, notes: round.notes, snapshot: row.savedNpd }}
                                          currentNpd={row.curNpd}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 bg-slate-50 text-[10px] text-slate-500 italic border-t border-slate-200">
        Read-only. Click "Components Total" to expand the per-component breakdown. Click any component to drill into its A/B/C/D/E section deltas. Amber warning marks rows that moved 10% or more.
      </div>
    </div>
  );
}

// R49 — Per-round assembly drawing screenshot thumbnail strip
function AssemblyRoundDrawingStrip({ assemblyId, round }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    api.get(`/assemblies/${assemblyId}/rounds/${round.round_id}/screenshot`, { responseType: "blob" })
      .then((r) => {
        const b = URL.createObjectURL(r.data);
        revoke = b;
        setUrl(b);
      })
      .catch(() => setUrl(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [assemblyId, round.round_id]);

  return (
    <div
      className="px-3 py-3 flex flex-wrap items-start gap-4 bg-slate-50 border-b border-slate-200"
      data-testid={`asm-round-drawing-${round.round_id}`}
    >
      <div className="flex-shrink-0">
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" title="Open screenshot in new tab">
            <img
              src={url}
              alt={`${round.round_label} assembly drawing screenshot`}
              className="w-32 h-32 object-cover border border-slate-300 rounded shadow-sm hover:shadow-md transition-shadow"
            />
          </a>
        ) : (
          <div className="w-32 h-32 flex items-center justify-center text-[10px] text-slate-400 border border-dashed border-slate-300 rounded bg-white">
            Loading…
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px] text-xs text-slate-600">
        <div className="uppercase tracking-wider text-slate-500 mb-1">Assembly drawing for {round.round_label}</div>
        <div className="text-[11px] text-slate-500">
          This is the assembly screenshot that was current when {round.round_label} was saved on {new Date(round.round_date).toLocaleString("en-IN")}.
          Subsequent uploads do not affect this round.
        </div>
      </div>
    </div>
  );
}

